import React, { useEffect, useMemo, useState } from 'react';
import { toast } from '../../../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../../../constants/DialogTypes';
import {
  Button,
  DataTable,
  Dialog,
  NameEmailAvatar,
} from '../../../../../../ui';
import { CloseIcon, SearchIcon } from '../../../../../../ui/icons/icons';
import inputStyles from '../../../../../../ui/input/input.module.css';
import {
  addToInviteList,
  getInviteList,
  removeFromInviteList,
  sendInvites,
} from '../../../../../../utils/api_service/JobOpenings';
import {
  InviteListItem,
  InviteListResponse,
} from '../../../../../../utils/types/JobOpenings';

interface InviteListModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobOpeningId: string;
}

const InviteListModal: React.FC<InviteListModalProps> = ({
  isOpen,
  onClose,
  jobOpeningId,
}) => {
  const [inviteList, setInviteList] = useState<InviteListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    search: '',
    applied: '',
    invited: '',
  });

  const fetchInviteList = async () => {
    if (!jobOpeningId) {
      setInviteList([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const applied =
        filters.applied === 'applied'
          ? true
          : filters.applied === 'not-applied'
            ? false
            : undefined;
      const response = await getInviteList(
        jobOpeningId,
        page,
        pageSize,
        filters.search || undefined,
        applied,
      );
      if (response.ok) {
        const data: InviteListResponse = await response.json();
        setInviteList(data.results);
        setTotal(data.total);
      } else {
        toast.error('Failed to fetch invite list');
      }
    } catch (error) {
      toast.error('Error fetching invite list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && jobOpeningId) {
      fetchInviteList();
    }
  }, [isOpen, jobOpeningId, page, filters]);

  const filteredCandidates = useMemo(() => {
    return inviteList.filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      const matchesSearch =
        !filters.search ||
        fullName.includes(filters.search.toLowerCase()) ||
        c.email.toLowerCase().includes(filters.search.toLowerCase());

      const matchesApplied =
        !filters.applied ||
        (filters.applied === 'applied' && c.applied) ||
        (filters.applied === 'not-applied' && !c.applied);

      const matchesInvited =
        !filters.invited ||
        (filters.invited === 'invited' && c.invited) ||
        (filters.invited === 'pending' && !c.invited);

      return matchesSearch && matchesApplied && matchesInvited;
    });
  }, [inviteList, filters]);

  const handleSelectRow = (rowIndex: number, row: InviteListItem) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(row.id)) {
      newSelected.delete(row.id);
    } else {
      newSelected.add(row.id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredCandidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCandidates.map((c) => c.id)));
    }
  };

  const handleSendInvite = async () => {
    if (selectedIds.size === 0) return;

    try {
      const response = await sendInvites(jobOpeningId, {
        inviteIds: Array.from(selectedIds),
      });
      if (response.ok) {
        toast.success(`Invites sent to ${selectedIds.size} candidates`);
        setSelectedIds(new Set());
        fetchInviteList();
      } else {
        toast.error('Failed to send invites');
      }
    } catch (error) {
      toast.error('Error sending invites');
    }
  };

  const handleSendAllInvites = async () => {
    try {
      const response = await sendInvites(jobOpeningId, { sendAll: true });
      if (response.ok) {
        toast.success('Invites sent to all pending candidates');
        fetchInviteList();
      } else {
        toast.error('Failed to send invites');
      }
    } catch (error) {
      toast.error('Error sending invites');
    }
  };

  const handleRemove = async () => {
    if (selectedIds.size === 0) return;

    // Only remove if not yet invited (as per API requirement)
    const candidatesToRemove = filteredCandidates.filter(
      (c) => selectedIds.has(c.id) && !c.invited,
    );

    if (candidatesToRemove.length === 0) {
      toast.warning('Cannot remove candidates who have already been invited');
      return;
    }

    try {
      const removePromises = candidatesToRemove.map((c) =>
        removeFromInviteList(jobOpeningId, c.id),
      );
      await Promise.all(removePromises);
      toast.success(
        `Removed ${candidatesToRemove.length} candidates from list`,
      );
      setSelectedIds(new Set());
      fetchInviteList();
    } catch (error) {
      toast.error('Error removing candidates from list');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Candidate Name',
      align: 'left' as const,
      render: (row: InviteListItem) => (
        <NameEmailAvatar
            name={`${row.firstName} ${row.lastName}`}
            email={row.email}
            avatar={''}
        />
      ),
    },
    {
      key: 'invitedAt',
      label: 'Invited On',
      render: (row: InviteListItem) => (
        <span className='text-gray-500'>
          { row.invited && row.invitedAt
            ? new Date(row.invitedAt * 1000).toLocaleDateString()
            : '—' }
        </span>
      ),
      align: 'left' as const,
    },
    {
      key: 'applied',
      label: 'Applied',
      render: (row: InviteListItem) => (
        <span
            className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border ${
              row.applied
                ? 'bg-green-50 text-green-600 border-green-100'
                : 'bg-gray-50 text-gray-600 border-gray-100'
            }`}
        >
          { row.applied ? 'Yes' : 'No' }
        </span>
      ),
      align: 'left' as const,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: InviteListItem) => (
        <span
            className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border ${
              row.invited
                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                : 'bg-gray-50 text-gray-600 border-gray-100'
            }`}
        >
          { row.invited ? 'Invited' : 'Pending' }
        </span>
      ),
      align: 'left' as const,
    },
  ];

  return (
    <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title='Candibank Invite List'
        dialogType={DIALOG_TYPE.SUCCESS}
        maxWidth='1200px'
        confirmButtonLable='Close'
        handleApply={onClose}
    >
      <div className='flex flex-col gap-4'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100'>
          <div className='flex flex-col gap-1.5'>
            <label className='text-xs font-bold text-gray-500'>Search</label>
            <div className='relative'>
              <input
                  className={`${inputStyles.inputStyles} !mt-0 !pl-9`}
                  type='text'
                  placeholder='Search by name or email...'
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
              />
              <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
                <SearchIcon className='w-4 h-4' />
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='text-xs font-bold text-gray-500'>
              Application Status
            </label>
            <select
                className={`${inputStyles.inputStyles} !mt-0`}
                value={filters.applied}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, applied: e.target.value }))
                }
            >
              <option value=''>All</option>
              <option value='applied'>Applied</option>
              <option value='not-applied'>Not Applied</option>
            </select>
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='text-xs font-bold text-gray-500'>
              Invite Status
            </label>
            <select
                className={`${inputStyles.inputStyles} !mt-0`}
                value={filters.invited || ''}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, invited: e.target.value }))
                }
            >
              <option value=''>All</option>
              <option value='invited'>Invited</option>
              <option value='pending'>Pending</option>
            </select>
          </div>
        </div>

        <div className='flex justify-between items-center px-1'>
          <div className='text-sm font-medium text-gray-600'>
            { selectedIds.size } candidates selected • { total } total
          </div>
          <div className='flex gap-3'>
            <Button
                label='Remove from List'
                variant='secondary'
                onClick={handleRemove}
                disabled={selectedIds.size === 0 || loading}
                className='!text-red-600 !border-red-200 hover:!bg-red-50'
            />
            <Button
                label='Send Selected Invites'
                variant='primary'
                onClick={handleSendInvite}
                disabled={selectedIds.size === 0 || loading}
            />
            <Button
                label='Send All Pending Invites'
                variant='primary'
                onClick={handleSendAllInvites}
                disabled={loading}
                className='!bg-green-600 hover:!bg-green-700'
            />
          </div>
        </div>

        <div className='h-[500px] overflow-hidden flex flex-col'>
          <DataTable
              data={filteredCandidates}
              columns={columns}
              isLoading={loading}
              hasCheckBox
              onSelectAll={handleSelectAll}
              onSelectRow={(index: number, row: InviteListItem) =>
                handleSelectRow(index, row)
              }
              isRowSelected={(index: number, row: InviteListItem) =>
                selectedIds.has(row.id)
              }
              isAllSelected={
                selectedIds.size > 0 &&
              selectedIds.size === filteredCandidates.length
              }
              isIndeterminate={
                selectedIds.size > 0 &&
              selectedIds.size < filteredCandidates.length
              }
              rowsPerPage={pageSize}
              currentPage={page}
              totalPages={Math.ceil(total / pageSize)}
              totalRecords={total}
              onPageChange={(newPage: number) => setPage(newPage)}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default InviteListModal;
